import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useMemo } from 'react';

import {
  useAddMutation,
  useDeleteMutation,
  useExchangeMutation,
} from '@app/api/SalesFunnelStageLead';
import { useLeadCardContext } from '@app/components/LeadCard/context';
import { SUCCESS } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useGetGlobalFilterFunnelsQuery } from '@app/services/rtkQuery/SalesFunnels/api';
import type { NFunnels } from '@app/types';

import { getStageColors } from '../utils/getStageColors';

const UNNAMED_FUNNEL = 'Без названия';
const UNNAMED_STAGE = 'Без названия';

export interface LeadFunnelStageRow {
  stageId: number;
  funnelName: string;
  stageName: string;
  background: string;
  textColor: string;
  funnel: NFunnels.SalesFunnelFilterDto | null;
}

export interface UseLeadFunnelStagesResult {
  rows: LeadFunnelStageRow[];
  hasStages: boolean;
  availableFunnelsForAdd: NFunnels.SalesFunnelFilterDto[];
  isFunnelsLoading: boolean;
  isFunnelsError: boolean;
  isMutating: boolean;
  canEdit: boolean;
  addStage(funnelStageId: number): Promise<void>;
  changeStage(currentStageId: number, newFunnelStageId: number): Promise<void>;
  removeStage(funnelStageId: number): Promise<void>;
}

export const useLeadFunnelStages = (): UseLeadFunnelStagesResult => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();
  const { leadCardData, refetchLeadCard, refreshLatestHistory } =
    useLeadCardContext();

  const leadId = leadCardData?.LeadId ?? null;
  const canEdit = leadId !== null && Boolean(ActiveBranchId);

  const {
    data: funnels,
    isLoading: isFunnelsLoading,
    isError: isFunnelsError,
  } = useGetGlobalFilterFunnelsQuery(
    ActiveBranchId ? { ActiveBranchId } : skipToken
  );

  const [addStageMutation, { isLoading: isAdding }] = useAddMutation();
  const [exchangeStageMutation, { isLoading: isExchanging }] =
    useExchangeMutation();
  const [deleteStageMutation, { isLoading: isDeleting }] = useDeleteMutation();

  const availableFunnels = useMemo(() => funnels ?? [], [funnels]);

  const assignedStages = useMemo<NFunnels.SalesFunnelStageFilterDto[]>(
    () => leadCardData?.Lead?.FunnelStages ?? [],
    [leadCardData?.Lead?.FunnelStages]
  );

  const findFunnelByStageId = useCallback(
    (stageId: number): NFunnels.SalesFunnelFilterDto | null =>
      availableFunnels.find((funnel) =>
        funnel.Stages?.some(({ Id }) => Id === stageId)
      ) ?? null,
    [availableFunnels]
  );

  const rows = useMemo<LeadFunnelStageRow[]>(
    () =>
      assignedStages.map((stage) => {
        const funnel = findFunnelByStageId(stage.Id);
        const { background, text } = getStageColors(stage.Color);

        return {
          stageId: stage.Id,
          funnelName: funnel ? funnel.Name || UNNAMED_FUNNEL : '',
          stageName: stage.Name || UNNAMED_STAGE,
          background,
          textColor: text,
          funnel,
        };
      }),
    [assignedStages, findFunnelByStageId]
  );

  const availableFunnelsForAdd = useMemo(() => {
    const assignedFunnelIds = new Set(
      rows.map(({ funnel }) => funnel?.Id).filter((id): id is number => !!id)
    );

    return availableFunnels.filter(
      (funnel) =>
        !assignedFunnelIds.has(funnel.Id) && (funnel.Stages?.length ?? 0) > 0
    );
  }, [availableFunnels, rows]);

  const addStage = useCallback(
    async (funnelStageId: number) => {
      if (leadId === null || !ActiveBranchId) return;

      try {
        await addStageMutation({
          ActiveBranchId,
          leadId,
          funnelStageId,
        }).unwrap();

        showNotification('Этап воронки добавлен', SUCCESS);
        refetchLeadCard();
        void refreshLatestHistory();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      ActiveBranchId,
      addStageMutation,
      leadId,
      refetchLeadCard,
      refreshLatestHistory,
      showNotification,
    ]
  );

  const changeStage = useCallback(
    async (currentStageId: number, newFunnelStageId: number) => {
      if (leadId === null || !ActiveBranchId) return;
      if (currentStageId === newFunnelStageId) return;

      try {
        await exchangeStageMutation({
          ActiveBranchId,
          leadId,
          newFunnelStageId,
        }).unwrap();

        showNotification('Этап воронки изменён', SUCCESS);
        refetchLeadCard();
        void refreshLatestHistory();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      ActiveBranchId,
      exchangeStageMutation,
      leadId,
      refetchLeadCard,
      refreshLatestHistory,
      showNotification,
    ]
  );

  const removeStage = useCallback(
    async (funnelStageId: number) => {
      if (leadId === null || !ActiveBranchId) return;

      try {
        await deleteStageMutation({
          ActiveBranchId,
          leadId,
          funnelStageId,
        }).unwrap();

        showNotification('Этап воронки удалён', SUCCESS);
        refetchLeadCard();
        void refreshLatestHistory();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      ActiveBranchId,
      deleteStageMutation,
      leadId,
      refetchLeadCard,
      refreshLatestHistory,
      showNotification,
    ]
  );

  return {
    rows,
    hasStages: assignedStages.length > 0,
    availableFunnelsForAdd,
    isFunnelsLoading,
    isFunnelsError,
    isMutating: isAdding || isExchanging || isDeleting,
    canEdit,
    addStage,
    changeStage,
    removeStage,
  };
};
